import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom, map, of, tap } from 'rxjs';
import { ProjectService } from '../../shared/services/project/project.service';
import { TimeSeriesTablesService } from '../../features/timeseries-data/services/timeseries-tables.service';
import { TimeSeriesTable } from '../../features/timeseries-data/models/timeseries-table.model';
import {
  AddTimeSeriesTableDialog,
  type AddTimeSeriesTableResult,
} from '../../features/timeseries-data/dialogs/add-timeseries-table-dialog/add-timeseries-table-dialog';
import {
  EditTimeSeriesTableDialog,
  type EditTimeSeriesTableResult,
} from '../../features/timeseries-data/dialogs/edit-timeseries-table-dialog/edit-timeseries-table-dialog';
import {
  SectionManagerDialog,
  type SectionManagerDialogAdapter,
  type SectionManagerDialogConfig,
} from '../../shared/common/section-manager-dialog/section-manager-dialog';

@Injectable({ providedIn: 'root' })
export class TimeSeriesTablesUiService {
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly projectService = inject(ProjectService);
  private readonly tablesService = inject(TimeSeriesTablesService);

  openAddTableDialog(): void {
    const currentProject = this.projectService.currentProject();
    if (!currentProject) return;

    this.dialog
      .open(AddTimeSeriesTableDialog)
      .afterClosed()
      .subscribe(async (result?: AddTimeSeriesTableResult) => {
        if (!result) return;

        const created = await firstValueFrom(
          this.tablesService.create({
            name: result.name,
            description: result.description || undefined,
            plantId: currentProject.plantMeta?.id ?? '',
            projectId: currentProject.id,
            fields: [],
          }),
        );

        this.tablesService.addTable(created);
        await this.router.navigate(['/timeseries-data', created.id]);
      });
  }

  openSectionManagerDialog(): void {
    const adapter: SectionManagerDialogAdapter = {
      loadItems: () =>
        of(
          [...this.tablesService.tables()]
            .sort((a, b) => a.order - b.order)
            .map((t) => ({ id: t.id, key: t.id, label: t.name, visible: t.visible })),
        ),
      createItem: (dto) => {
        const currentProject = this.projectService.currentProject();
        return this.tablesService
          .create({
            name: dto.label,
            plantId: currentProject?.plantMeta?.id ?? '',
            projectId: currentProject?.id ?? '',
            fields: [],
          })
          .pipe(
            tap((created) => this.tablesService.addTable(created)),
            map((created) => ({
              id: created.id,
              key: created.id,
              label: created.name,
              visible: created.visible ?? true,
            })),
          );
      },
      updateItem: (id, dto) =>
        this.tablesService
          .update(id, { name: dto.label, order: dto.order, visible: dto.visible })
          .pipe(tap((updated) => this.tablesService.updateTable(updated))),
      deleteItem: (item) =>
        this.tablesService.remove(item.id).pipe(
          tap(() => {
            this.tablesService.removeTable(item.id);
            if (this.router.url.includes(item.id)) {
              void this.router.navigate(['/timeseries-data']);
            }
          }),
        ),
    };

    this.dialog.open(SectionManagerDialog, {
      width: '560px',
      data: { adapter, hideKeys: true } satisfies SectionManagerDialogConfig,
    });
  }

  openEditTableDialog(table: TimeSeriesTable): void {
    this.dialog
      .open(EditTimeSeriesTableDialog, {
        data: {
          id: table.id,
          name: table.name,
          description: table.description,
          timezone: table.timezone,
          timestampField: table.timestampField,
          duplicateHandling: table.duplicateHandling,
          period: table.period,
          allowEditSubmittedRows: table.allowEditSubmittedRows,
        },
      })
      .afterClosed()
      .subscribe(async (result?: EditTimeSeriesTableResult) => {
        if (!result) return;

        if (result.action === 'delete') {
          await firstValueFrom(this.tablesService.remove(result.id));
          this.tablesService.removeTable(result.id);
          await this.router.navigate(['/timeseries-data']);
          return;
        }

        const updated = await firstValueFrom(
          this.tablesService.update(table.id, {
            name: result.name,
            description: result.description || undefined,
            ingestion: {
              timezone: result.timezone,
              timestampField: result.timestampField,
              duplicateHandling: result.duplicateHandling,
              period: result.period,
              allowEditSubmittedRows: result.allowEditSubmittedRows,
            },
          }),
        );

        this.tablesService.updateTable(updated);
      });
  }
}
